import React, { useEffect, useState } from "react";
import {
  DotsVerticalIcon,
  PencilAltIcon,
  PlusIcon,
  SwitchHorizontalIcon,
  MenuIcon, // Import the hamburger icon
} from "@heroicons/react/outline";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import {
  requestListBusiness,
  useAuthState,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
} from "../../../../Context";
import { useFreeBusinessCreateBlocked } from "../../../../Hooks";
import BusinessSwitchModal from "../BusinessSwitchModal";
import BusinessAvatar from "../BusinessAvatar";
import BusinessFormModal from "../BusinessFormModal";
import InitAccountPromptModal from "../../Accounting/InitAccountPromptModal";
import { ModalConfirm } from "../../../ModalConfirm";

const BusinessHeader = ({ onToggleSidebar }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // The accounting dashboard raises its own prompt for any active business
  // without accounts; prompting here too would stack two identical modals.
  const dashboardPrompts = pathname === "/v2/accounting";

  const [switchBusinessOpen, setSwitchBusinessOpen] = useState(false);
  const [businessModalOpen, setBusinessModalOpen] = useState(false);
  const [editBusinessId, setEditBusinessId] = useState(undefined);
  // Business id awaiting the skippable init-account prompt after creation.
  const [initPromptBusinessId, setInitPromptBusinessId] = useState(null);
  const [freeLimitModalOpen, setFreeLimitModalOpen] = useState(false);

  const { user, loading, getUserLoading } = useAuthState();
  const businessDispatch = useBusinessTransactionDispatch();
  const { businesses } = useBusinessTransactionState();

  // Load the owner's businesses so the free-limit create gate has a reliable
  // count regardless of whether a main business is set.
  useEffect(() => {
    if (user?.id) {
      requestListBusiness(businessDispatch, { owner: user.id });
    }
  }, [businessDispatch, user?.id]);

  const createBlocked = useFreeBusinessCreateBlocked(businesses);

  const handleCreateClick = () => {
    if (createBlocked) {
      setFreeLimitModalOpen(true);
      return;
    }
    setEditBusinessId(undefined);
    setBusinessModalOpen(true);
  };

  const handleCloseModal = () => {
    setBusinessModalOpen(false);
    setEditBusinessId(undefined);
  };

  const itemStyle =
    "w-full data-[focus]:bg-gray-100 text-left px-4 py-2 text-sm text-gray-700 flex items-center";

  return (
    <div className="px-4 sm:px-6 mb-6 flex items-center justify-between">
      <div className="flex items-center min-w-0 flex-1">
        <BusinessAvatar
          loading={loading || getUserLoading}
          business={user?.mainBusiness}
        />
      </div>

      <Menu>
        <div>
          <MenuButton className="p-1 rounded-full hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2">
            <DotsVerticalIcon
              className="h-6 w-6 text-gray-500"
              aria-hidden="true"
            />
          </MenuButton>
          <button
            onClick={onToggleSidebar}
            className="p-1 rounded-full hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 mr-2 lg:hidden"
          >
            <MenuIcon className="h-6 w-6 text-gray-500" aria-hidden="true" />
          </button>
        </div>

        <MenuItems
          transition
          anchor="bottom end"
          className="origin-top-right w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 [--anchor-gap:4px] sm:[--anchor-gap:8px] focus:outline-none transition duration-100 ease-out data-[closed]:scale-95 data-[closed]:opacity-0"
        >
          <div className="py-1">
            <MenuItem>
              <button onClick={handleCreateClick} className={itemStyle}>
                <PlusIcon
                  className="mr-3 h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
                {t("businesses.create")}
              </button>
            </MenuItem>
            <MenuItem>
              <button
                onClick={() => {
                  setEditBusinessId(user?.mainBusiness?.id);
                  setBusinessModalOpen(true);
                }}
                className={itemStyle}
              >
                <PencilAltIcon
                  className="mr-3 h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
                {t("businesses.edit")}
              </button>
            </MenuItem>
            <MenuItem>
              <button
                onClick={() => setSwitchBusinessOpen(true)}
                className={itemStyle}
              >
                <SwitchHorizontalIcon
                  className="mr-3 h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
                {t("businesses.switch")}
              </button>
            </MenuItem>
          </div>
        </MenuItems>
      </Menu>

      <BusinessSwitchModal
        open={switchBusinessOpen}
        onClose={() => setSwitchBusinessOpen(false)}
      />
      {businessModalOpen && (
        <BusinessFormModal
          open={businessModalOpen}
          onClose={handleCloseModal}
          id={editBusinessId}
          onCreated={(newBusiness) => {
            handleCloseModal();
            if (newBusiness?.id && !dashboardPrompts)
              setInitPromptBusinessId(newBusiness.id);
          }}
        />
      )}
      {initPromptBusinessId && (
        <InitAccountPromptModal
          open={Boolean(initPromptBusinessId)}
          businessID={initPromptBusinessId}
          onSkip={() => setInitPromptBusinessId(null)}
        />
      )}
      <ModalConfirm
        open={freeLimitModalOpen}
        setOpen={setFreeLimitModalOpen}
        title={t("business.messages.freeLimitBlockTitle")}
        description={t("business.messages.freeLimitBlockDesc")}
        type="WARNING"
        confirmTitle={t("business.actions.subscribe")}
        onConfirm={() => {
          setFreeLimitModalOpen(false);
          navigate("/subscription");
        }}
      />
    </div>
  );
};

export default BusinessHeader;
